import { useCallback, useEffect, useState } from "react";
import {
  api,
  ApiError,
  type CohortAutoStatus,
  type CohortCandidate,
  type CohortPreview,
  type CohortRun,
} from "../lib/api.ts";
import { ago, money, monthLabel, num } from "../lib/format.ts";
import { Card } from "./widgets.tsx";
import { Th, useSortable } from "./sortable.tsx";
import { currentLang, dbLabel, plural, t, type L } from "../i18n/lang.ts";
import { SENTINEL, SKIP_REASON, UI } from "../i18n/dict.ts";

/**
 * Client base — реактивация базы клиентов.
 *
 * Первая когорта: клиенты, отпраздновавшие у нас год назад. День рождения
 * повторяется ежегодно, поэтому это самая тёплая база, какая у сети есть.
 * Руководитель отдела продаж просила довести её до менеджеров, потому что
 * вручную за этим не следит: «есть прошлая база, свяжитесь. Но я не могу за
 * ручку всех держать».
 *
 * Устройство экрана держится на одном правиле: **сначала показать, потом
 * создавать**. Создание нельзя отменить — лиды появляются у людей в работе, и
 * людям уходят уведомления. Поэтому кнопка становится доступной только после
 * предпросмотра, в ней написано точное число, а пропущенных видно с причиной.
 *
 * Сверху — рубильник автозапуска: 1-го числа система сама делает то же, что
 * кнопка, для того же месяца год назад. Предпросмотр там заменяет фраза о
 * последствиях: когда сработает, какая когорта и сколько в ней сейчас, — её
 * человек читает ДО щелчка, и щелчок сам по себе ничего не создаёт.
 */

/** Проза этого экрана. Один блок в начале файла — см. App.tsx. */
const TX = {
  blockedByLead: { ru: "лид", en: "lead" },
  blockedCreated: { ru: "создан", en: "created" },
  /* --- когорта и окно
   *
   * «Rebooking» не переводится: этим словом когорта названа в системе
   * (`meta.cohort_run.cohort`, `/api/cohorts/rebooking`), и по нему же её
   * называют в отделе продаж. */
  rebookingCohort: { ru: "Когорта Rebooking", en: "Rebooking cohort" },
  cohortHint: {
    ru: "клиенты, у которых банкет прошёл в выбранном окне год назад",
    en: "clients whose banquet took place in the chosen window a year ago",
  },
  eventFrom: { ru: "Праздник с", en: "Event from" },
  eventTo: { ru: "по", en: "to" },
  looking: { ru: "смотрим…", en: "looking…" },
  showCohort: { ru: "Показать когорту", en: "Show cohort" },
  create: { ru: "Создать", en: "Create" },
  /* Счётные существительные: в русском три формы. */
  leadsWord: {
    one: { ru: "лид", en: "lead" },
    few: { ru: "лида", en: "leads" },
    many: { ru: "лидов", en: "leads" },
  },
  clientsWord: {
    one: { ru: "клиент", en: "client" },
    few: { ru: "клиента", en: "clients" },
    many: { ru: "клиентов", en: "clients" },
  },

  /* --- предупреждение перед кнопкой, которая ПИШЕТ В БОЕВУЮ CRM
   *
   * Это последнее, что человек читает до нажатия: созданные лиды появляются у
   * живых менеджеров, и людям уходят уведомления. Ни смягчать, ни сокращать. */
  windowMatchesOn: { ru: "Окно ищет по", en: "The window matches on the" },
  eventDateBold: { ru: "дате праздника", en: "event date" },
  nothingCreatedUntilPressed: {
    ru: ", а не по дате закрытия сделки: день рождения приходится на ту же неделю каждый год. Ничего не создаётся, пока вы не нажмёте вторую кнопку, и на кнопке написано, сколько именно.",
    en: ", not the date the deal was closed: a birthday comes round the same week every year. Nothing is created until you press the second button, and the button says exactly how many.",
  },

  /* --- итог прогона.
   *
   * До двуязычия это сообщение было только по-русски — на английском экране.
   * Тот же класс дефекта, что описан в `i18n/dict.ts` у SETTING. */
  msgLeadsCreated: { ru: "Создано лидов:", en: "Leads created:" },
  msgFailed: { ru: "Не удалось:", en: "Failed:" },
  msgNotified: { ru: "Уведомлено менеджеров:", en: "Managers notified:" },
  msgRun: { ru: "Прогон №", en: "Run #" },

  /* --- сводка предпросмотра */
  inTheCohort: { ru: "В когорте", en: "In the cohort" },
  clientsCelebrated: {
    one: {
      ru: "клиент, отпраздновавший у нас в этом окне",
      en: "client who celebrated with us in that window",
    },
    few: {
      ru: "клиента, отпраздновавших у нас в этом окне",
      en: "clients who celebrated with us in that window",
    },
    many: {
      ru: "клиентов, отпраздновавших у нас в этом окне",
      en: "clients who celebrated with us in that window",
    },
  },
  canBeCreated: { ru: "Можно создать", en: "Can be created" },
  selectedRightNow: { ru: "выбрано прямо сейчас", en: "selected right now" },
  skipped: { ru: "Пропущено", en: "Skipped" },
  withReasonBelow: { ru: "с причиной, она ниже", en: "with a reason, listed below" },

  /* --- по паркам */
  byPark: { ru: "По паркам", en: "By park" },
  whoGetsTheWork: { ru: "кому достанется работа", en: "who gets the work" },
  assignmentNote: {
    ru: "Лид достаётся менеджеру ОП, который работал с этим клиентом год назад, если он всё ещё ведёт этот парк. Остальные распределяются равномерно по текущей загрузке — не случайно, поэтому одни и те же входные данные всегда дают одно и то же распределение, и прогон можно объяснить потом.",
    en: "A lead goes to the salesperson who worked with that client last year, if they still handle that park. Everyone else is spread evenly by current load — not at random, so the same input always produces the same split and a run can be explained afterwards.",
  },

  /* --- кого создадим */
  willBeCreated: { ru: "Будут созданы", en: "Will be created" },
  untickAnyone: {
    ru: "снимите галочку с тех, кого включать не надо",
    en: "untick anyone you want left out",
  },
  includeAria: { ru: "включить", en: "include" },
  colClient: { ru: "Клиент", en: "Client" },
  colPhone: { ru: "Телефон", en: "Phone" },
  colCelebrated: { ru: "Праздновали", en: "Celebrated" },
  colThenPaidAed: { ru: "Тогда оплатили, AED", en: "Then paid, AED" },
  colWorkedBy: { ru: "С кем работали", en: "Worked by" },
  noName: { ru: "(без имени)", en: "(no name)" },

  /* --- кого пропустили */
  skippedAndWhy: { ru: "Пропущено, и почему", en: "Skipped, and why" },
  colReason: { ru: "Причина", en: "Reason" },
  colWhatIsInTheWay: { ru: "Что мешает", en: "What is in the way" },
  alreadyWorkedNote: {
    ru: "Клиента, с которым уже работают, намеренно оставляем в покое: второй лид на того же человека — это два менеджера ОП, звонящих ему одновременно. Мешающий лид назван, чтобы вы могли судить сами: заброшенный месяцы назад — это одно, а открытый вчера — совсем другое.",
    en: "A client already being worked is left alone on purpose: a second lead on the same person means two salespeople phoning them. The blocking lead is named so you can judge for yourself — one abandoned months ago is a different matter from one opened yesterday.",
  },

  /* --- журнал прогонов */
  previousRuns: { ru: "Прошлые прогоны", en: "Previous runs" },
  whatWasCreated: { ru: "что создано и кем", en: "what was created, by whom" },
  colStarted: { ru: "Начат", en: "Started" },
  colWindow: { ru: "Окно", en: "Window" },
  colRequested: { ru: "Запрошено", en: "Requested" },
  colCreated: { ru: "Создано", en: "Created" },
  colFailed: { ru: "Не удалось", en: "Failed" },
  colBy: { ru: "Кем", en: "By" },
  byAuto: { ru: "автозапуск 1-го числа", en: "auto-run on the 1st" },

  /* --- отказы кнопки по коду: фразу сервер шлёт для журнала, экран печатает свою */
  errBusy: {
    ru: "Прямо сейчас лиды по базе уже создаются — возможно, автозапуском 1-го числа. Повторите через пару минут: созданных к тому времени кнопка не тронет.",
    en: "Leads from the base are being created right now — possibly by the auto-run on the 1st. Try again in a couple of minutes: anyone created by then will be left alone.",
  },
  errOverCapA: { ru: "Выбрано", en: "Selected:" },
  errOverCapB: { ru: ", а потолок за один раз —", en: ", but the cap per run is" },
  errOverCapC: {
    ru: ". Снимите часть галочек или поднимите потолок на экране Administration.",
    en: ". Untick some, or raise the cap on the Administration screen.",
  },

  /* --- автозапуск 1-го числа
   *
   * Рубильник пишет в боевую CRM без чьего-либо нажатия в момент записи,
   * поэтому всё, что он сделает, сказано рядом с ним заранее: когда, какая
   * когорта, сколько в ней сейчас, что будет с теми, кто не влез в потолок. */
  autoTitle: { ru: "Автозапуск 1-го числа", en: "Auto-run on the 1st" },
  autoHint: {
    ru: "лиды по тому же месяцу год назад — без нажатия",
    en: "leads from the same month last year — without a click",
  },
  autoOff: { ru: "Выключено", en: "Off" },
  autoOn: { ru: "Включено", en: "On" },
  autoAria: { ru: "Автозапуск 1-го числа", en: "Auto-run on the 1st" },
  autoNextOn: { ru: "Следующий запуск —", en: "Next run —" },
  autoNextOff: {
    ru: "Выключено. Если включить сейчас, первый запуск —",
    en: "Off. Switched on now, the first run would be",
  },
  autoDubai: { ru: "по Дубаю.", en: "Dubai time." },
  autoDueA: {
    ru: "Пора: запуск сработает при ближайшей проверке, в пределах",
    en: "Due now: the run starts at the next check, within",
  },
  autoDueB: { ru: "минут.", en: "minutes." },
  autoResumeA: { ru: "Идёт автозапуск за", en: "The run for" },
  autoResumeB: { ru: ": создано", en: " is in progress:" },
  autoResumeOf: { ru: "из", en: "of" },
  autoResumeC: {
    ru: ", остальное — при следующей проверке.",
    en: " created, the rest at the next check.",
  },
  autoCohort: { ru: "Когорта:", en: "Cohort:" },
  autoCohortNow: { ru: "— сейчас в ней", en: "— right now it has" },
  autoCanCreate: { ru: "создать можно", en: "eligible for a lead:" },
  autoRecount: {
    ru: "Число пересчитается в момент запуска.",
    en: "The figure is recounted when the run starts.",
  },
  autoOverCapA: { ru: "Это больше потолка —", en: "That is above the cap of" },
  autoOverCapB: {
    ru: "за запуск: создадутся ближайшие по дате праздника, остальные останутся для кнопки ниже.",
    en: "per run: the nearest celebration dates go first, the rest stay for the button below.",
  },
  autoRules: {
    ru: "Правила те же, что у кнопки ниже: клиентов, с которыми уже работают, и тех, у кого нет телефона, пропускает; повторно по той же сделке лид не создаёт; лид отдаёт прежнему менеджеру, если тот всё ещё ведёт парк, остальных — ровно; каждому менеджеру — одно уведомление. Включение посреди месяца ничего не создаёт сразу: текущий месяц делается кнопкой.",
    en: "Same rules as the button below: clients already being worked and clients without a phone are skipped; no second lead from the same deal; a lead goes to the previous manager if they still handle the park, the rest are spread evenly; each manager gets one notification. Switching on mid-month creates nothing straight away: the current month is done with the button.",
  },
  autoLast: { ru: "Последний автозапуск —", en: "Last auto-run —" },
  autoLastCreated: { ru: "создано", en: "created" },
  autoLastFailed: { ru: "не удалось", en: "failed" },
  autoLastSkipped: { ru: "пропущено", en: "skipped" },
  autoNever: { ru: "Автозапусков ещё не было.", en: "No auto-runs yet." },
  autoSwitched: { ru: "Последнее переключение:", en: "Last switched by" },
  autoEvery: { ru: "Проверка каждые", en: "Checked every" },
  autoMin: { ru: "мин", en: "min" },
  autoLastCheck: { ru: "последняя —", en: "last" },
  autoNoScheduler: {
    ru: "Планировщик выключен (scheduler_enabled на экране Administration) — автозапуск не сработает.",
    en: "The scheduler is off (scheduler_enabled on the Administration screen) — the auto-run will not fire.",
  },
  autoNoSchedule: {
    ru: "Проверка автозапуска выключена в расписании — автозапуск не сработает.",
    en: "The auto-run check is disabled in the schedule — the auto-run will not fire.",
  },
  autoStale: {
    ru: "Проверка давно не проходила — похоже, планировщик сейчас не работает, и автозапуск не сработает. Последняя проверка:",
    en: "The check has not run for a while — the scheduler looks stopped, so the auto-run will not fire. Last check:",
  },
  autoCheckFailed: { ru: "Последняя проверка закончилась ошибкой:", en: "The last check failed:" },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

const TODAY_YEAR = new Date().getFullYear();

export function ClientBaseScreen(): React.ReactElement {
  /** По умолчанию — тот же месяц год назад: именно эта когорта и нужна. */
  const [from, setFrom] = useState(`${TODAY_YEAR - 1}-${pad(new Date().getMonth() + 1)}-01`);
  const [to, setTo] = useState(`${TODAY_YEAR - 1}-${pad(new Date().getMonth() + 1)}-${daysIn(TODAY_YEAR - 1, new Date().getMonth() + 1)}`);
  const [preview, setPreview] = useState<CohortPreview | null>(null);
  const [runs, setRuns] = useState<CohortRun[]>([]);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadRuns = useCallback(async () => {
    try {
      setRuns((await api.cohortRuns()).runs);
    } catch {
      /* журнал прогонов не критичен для работы экрана */
    }
  }, []);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  const look = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.cohortPreview(from, to);
      setPreview(r);
      // По умолчанию выбраны все годные: это то, зачем экран открывают.
      setChosen(new Set(r.candidates.filter((c) => !c.skip_reason).map((c) => c.source_deal_id)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (chosen.size === 0) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.cohortCreate(from, to, [...chosen]);
      setMessage(
        `${t(TX.msgLeadsCreated)} ${r.created}. ${t(TX.msgFailed)} ${r.failed}. ` +
          `${t(TX.msgNotified)} ${r.notified}. ${t(TX.msgRun)}${r.runId}.`,
      );
      await Promise.all([look(), loadRuns()]);
    } catch (e) {
      setError(createError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string) => {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChosen(next);
  };

  const eligible = preview?.candidates.filter((c) => !c.skip_reason) ?? [];
  const skipped = preview?.candidates.filter((c) => c.skip_reason) ?? [];

  const park = useSortable(preview?.byPark ?? [], {
    park: { kind: "text", get: (b) => dbLabel(SENTINEL, b.park_name, b.park_name) },
    eligible: { kind: "number", get: (b) => b.eligible },
    skipped: { kind: "number", get: (b) => b.skipped },
  });

  /*
   * СОРТИРОВКА КАНДИДАТОВ НЕ ТРОГАЕТ ВЫБОР, и это свойство самого выбора, а не
   * удача: отмеченные лежат в `chosen` как множество `source_deal_id` (см.
   * `look()` и `toggle()` выше), галочка читается `chosen.has(c.source_deal_id)`,
   * ключ строки — тот же идентификатор, а кнопка создания отправляет `[...chosen]`
   * и порядок строк вообще не видит. Сортировка переставляет массив — привязки
   * «строка ↔ отметка» это не касается. Если бы выбор хранился номером строки,
   * первый же клик по заголовку переставил бы галочки на других людей, и лиды
   * создались бы не тем.
   *
   * Кандидаты приходят целиком: запрос когорты идёт без `LIMIT`
   * (`api/cohorts.ts:106—197`), поэтому сортировать их честно.
   */
  const cand = useSortable(eligible, {
    /* «(без имени)» — фраза-заменитель, а не имя: такие строки уходят в конец. */
    client: { kind: "text", get: (c) => c.title },
    phone: { kind: "text", get: (c) => c.phone_e164 },
    park: { kind: "text", get: (c) => c.park_name },
    celebrated: { kind: "text", get: (c) => c.event_date },
    paid: { kind: "number", get: (c) => c.amount_aed_minor },
    manager: { kind: "text", get: (c) => c.previous_manager_name },
  });

  const skip = useSortable(skipped, {
    client: { kind: "text", get: (c) => c.title },
    park: { kind: "text", get: (c) => c.park_name },
    reason: { kind: "text", get: (c) => dbLabel(SKIP_REASON, c.skip_reason, c.skip_reason) },
    /* «Что мешает» ячейка собирает фразой «лид 4711, Interested, создан …».
       Сравниваем номер мешающего лида: фраза начинается с него после одного
       постоянного слова, поэтому порядок по номеру и есть порядок напечатанного —
       и при этом фраза не переписана здесь во второй раз. */
    blocked: { kind: "number", get: (c) => c.blocked_by?.lead_id ?? null },
  });

  /*
   * ЖУРНАЛ ПРОГОНОВ СОРТИРОВКИ НЕ ПОЛУЧАЕТ: сервер отдаёт последние двадцать
   * (`server/src/index.ts:688` — `ORDER BY r.started_at DESC LIMIT 20`), а
   * «показать ещё» у журнала нет. Прогоны накапливаются с каждым нажатием
   * кнопки, и на двадцать первом клик по «Создано» показал бы самый крупный
   * прогон из последних двадцати как самый крупный за всё время. Серверной
   * сортировки у этого запроса нет, поэтому таблица помечена `partial`:
   * заголовки не кликаются и говорят, почему.
   */
  const run = useSortable(
    runs,
    {
      started: { kind: "text", get: (r) => r.started_at },
      window: { kind: "text", get: (r) => r.event_from },
      requested: { kind: "number", get: (r) => r.requested },
      created: { kind: "number", get: (r) => r.created },
      /* Ноль неудач ячейка печатает прочерком — значит для сортировки его нет. */
      failed: { kind: "number", get: (r) => (r.failed > 0 ? r.failed : null) },
      by: { kind: "text", get: (r) => runBy(r) },
    },
    { partial: true },
  );

  return (
    <>
      <AutoRunCard />

      <Card title={t(TX.rebookingCohort)} hint={t(TX.cohortHint)}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label>
            <span className="muted" style={{ fontSize: 12, marginRight: 6 }}>
              {t(TX.eventFrom)}
            </span>
            <input type="text" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 110, padding: "4px 7px" }} />
          </label>
          <label>
            <span className="muted" style={{ fontSize: 12, marginRight: 6 }}>
              {t(TX.eventTo)}
            </span>
            <input type="text" value={to} onChange={(e) => setTo(e.target.value)} style={{ width: 110, padding: "4px 7px" }} />
          </label>
          <button className="btn" disabled={busy} onClick={() => void look()}>
            {busy ? t(TX.looking) : t(TX.showCohort)}
          </button>
          {preview && (
            <button className="btn primary" disabled={busy || chosen.size === 0} onClick={() => void create()}>
              {t(TX.create)} {num(chosen.size)} {plural(chosen.size, TX.leadsWord)}
            </button>
          )}
        </div>

        <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
          {t(TX.windowMatchesOn)} <b>{t(TX.eventDateBold)}</b>
          {t(TX.nothingCreatedUntilPressed)}
        </p>

        {message && <div className="note" style={{ marginTop: 10 }}>{message}</div>}
        {error && <div className="note" style={{ marginTop: 10, color: "var(--bad)" }}>{error}</div>}
      </Card>

      {preview && (
        <>
          <div className="grid cols-3" style={{ margin: "14px 0" }}>
            <Card title={t(TX.inTheCohort)}>
              <div style={{ fontSize: 26, fontWeight: 600 }}>{num(preview.summary.total)}</div>
              <div className="muted" style={{ fontSize: 12 }}>
                {plural(preview.summary.total, TX.clientsCelebrated)}
              </div>
            </Card>
            <Card title={t(TX.canBeCreated)}>
              <div style={{ fontSize: 26, fontWeight: 600 }}>{num(preview.summary.eligible)}</div>
              <div className="muted" style={{ fontSize: 12 }}>
                {num(chosen.size)} {t(TX.selectedRightNow)}
              </div>
            </Card>
            <Card title={t(TX.skipped)}>
              <div style={{ fontSize: 26, fontWeight: 600 }}>{num(preview.summary.skipped)}</div>
              <div className="muted" style={{ fontSize: 12 }}>{t(TX.withReasonBelow)}</div>
            </Card>
          </div>

          <Card title={t(TX.byPark)} hint={t(TX.whoGetsTheWork)}>
            <table className="data">
              <thead>
                <tr>
                  <Th sort={park} col="park">{t(UI.park)}</Th>
                  <Th sort={park} col="eligible" num>{t(TX.canBeCreated)}</Th>
                  <Th sort={park} col="skipped" num>{t(TX.skipped)}</Th>
                </tr>
              </thead>
              <tbody>
                {park.rows.map((b) => (
                  <tr key={b.park_key}>
                    {/* Имя парка не переводится; переводится только то, чем
                        сервер обозначает «парк не определён». */}
                    <td>{dbLabel(SENTINEL, b.park_name, b.park_name)}</td>
                    <td className="num">{num(b.eligible)}</td>
                    <td className="num">{num(b.skipped)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="note" style={{ marginTop: 12 }}>
              {t(TX.assignmentNote)}
            </div>
          </Card>

          <Card
            title={t(TX.willBeCreated)}
            hint={`${num(eligible.length)} ${plural(eligible.length, TX.clientsWord)} · ${t(TX.untickAnyone)}`}
            className="span-2"
          >
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    {/* Колонка галочек не сортируется: это орган управления, а
                        не значение строки. */}
                    <th />
                    <Th sort={cand} col="client">{t(TX.colClient)}</Th>
                    <Th sort={cand} col="phone">{t(TX.colPhone)}</Th>
                    <Th sort={cand} col="park">{t(UI.park)}</Th>
                    <Th sort={cand} col="celebrated">{t(TX.colCelebrated)}</Th>
                    <Th sort={cand} col="paid" num>{t(TX.colThenPaidAed)}</Th>
                    <Th sort={cand} col="manager">{t(TX.colWorkedBy)}</Th>
                  </tr>
                </thead>
                <tbody>
                  {cand.rows.map((c) => (
                    <CandidateRow key={c.source_deal_id} c={c} checked={chosen.has(c.source_deal_id)} onToggle={toggle} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {skipped.length > 0 && (
            <Card
              title={t(TX.skippedAndWhy)}
              hint={`${num(skipped.length)} ${plural(skipped.length, TX.clientsWord)}`}
              className="span-2"
            >
              <div className="scroll-x">
                <table className="data">
                  <thead>
                    <tr>
                      <Th sort={skip} col="client">{t(TX.colClient)}</Th>
                      <Th sort={skip} col="park">{t(UI.park)}</Th>
                      <Th sort={skip} col="reason">{t(TX.colReason)}</Th>
                      <Th sort={skip} col="blocked">{t(TX.colWhatIsInTheWay)}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {skip.rows.map((c) => (
                      <tr key={c.source_deal_id}>
                        <td>{c.title ?? t(TX.noName)}</td>
                        <td>{c.park_name ?? "—"}</td>
                        {/* Причина приходит ключом, фразу подставляет словарь. */}
                        <td>{dbLabel(SKIP_REASON, c.skip_reason, c.skip_reason)}</td>
                        {/* Мешающий лид собирается ЗДЕСЬ из частей: склеенную на
                            сервере фразу перевести было бы нечем. Имя стадии
                            внутри — зеркало Битрикса, оно не переводится. */}
                        <td className="muted" style={{ fontSize: 12 }}>
                          {c.blocked_by === null
                            ? "—"
                            : `${t(TX.blockedByLead)} ${c.blocked_by.lead_id}, ` +
                              `${c.blocked_by.stage}, ${t(TX.blockedCreated)} ${c.blocked_by.created}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="note" style={{ marginTop: 12 }}>
                {t(TX.alreadyWorkedNote)}
              </div>
            </Card>
          )}
        </>
      )}

      {runs.length > 0 && (
        <Card title={t(TX.previousRuns)} hint={t(TX.whatWasCreated)} className="span-2">
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={run} col="started">{t(TX.colStarted)}</Th>
                  <Th sort={run} col="window">{t(TX.colWindow)}</Th>
                  <Th sort={run} col="requested" num>{t(TX.colRequested)}</Th>
                  <Th sort={run} col="created" num>{t(TX.colCreated)}</Th>
                  <Th sort={run} col="failed" num>{t(TX.colFailed)}</Th>
                  <Th sort={run} col="by">{t(TX.colBy)}</Th>
                </tr>
              </thead>
              <tbody>
                {run.rows.map((r) => (
                  <tr key={r.id}>
                    {/* Время прогона — в Дубае: парки там. Локаль идёт за языком
                        интерфейса, как в `lib/format.ts`. */}
                    <td className="muted">
                      {new Date(r.started_at).toLocaleString(
                        currentLang() === "ru" ? "ru-RU" : "en-GB",
                        { timeZone: "Asia/Dubai" },
                      )}
                    </td>
                    <td>
                      {r.event_from} → {r.event_to}
                    </td>
                    <td className="num">{num(r.requested)}</td>
                    <td className="num">{num(r.created)}</td>
                    <td className="num">{r.failed > 0 ? num(r.failed) : "—"}</td>
                    {/* Автозапуск назван словами: «auto» в колонке «Кем» читалось бы
                        как чьё-то имя. */}
                    <td className="muted">{runBy(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

function CandidateRow({
  c,
  checked,
  onToggle,
}: {
  c: CohortCandidate;
  checked: boolean;
  onToggle: (id: string) => void;
}): React.ReactElement {
  return (
    <tr>
      <td>
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(c.source_deal_id)}
          aria-label={`${t(TX.includeAria)} ${c.title ?? c.source_deal_id}`}
        />
      </td>
      <td>{c.title ?? t(TX.noName)}</td>
      <td className="muted">{c.phone_e164 ?? "—"}</td>
      <td>{c.park_name ?? "—"}</td>
      <td className="muted">{c.event_date ?? "—"}</td>
      <td className="num">{c.amount_aed_minor ? money(Number(c.amount_aed_minor), "AED", { withCode: false }) : "—"}</td>
      <td className="muted">{c.previous_manager_name ?? "—"}</td>
    </tr>
  );
}

/** Кто запустил прогон — словами: имя человека или «автозапуск 1-го числа». */
function runBy(r: CohortRun): string {
  return r.auto_month !== null ? t(TX.byAuto) : r.started_by_name ?? r.started_by;
}

/** Отказ кнопки создания — словами интерфейса по коду, иначе как прислал сервер. */
function createError(e: unknown): string {
  if (e instanceof ApiError && e.code === "busy") return t(TX.errBusy);
  if (e instanceof ApiError && e.code === "over_cap") {
    return `${t(TX.errOverCapA)} ${num(Number(e.body.chosen))}${t(TX.errOverCapB)} ${num(Number(e.body.cap))}${t(TX.errOverCapC)}`;
  }
  return e instanceof Error ? e.message : String(e);
}

/** «1 ноября 2026 г. в 09:00» — время парков, то есть Дубая, на языке интерфейса. */
function dubaiMoment(iso: string): string {
  return new Date(iso).toLocaleString(currentLang() === "ru" ? "ru-RU" : "en-GB", {
    timeZone: "Asia/Dubai",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Рубильник автозапуска и то, что из него следует.
 *
 * Переключатель стоит в шапке карточки справа — там, где его ищут. Всё, что
 * он сделает, написано под ним ДО щелчка: когда сработает, какая когорта,
 * сколько в ней сейчас и что будет с теми, кто не влез в потолок. Ниже — жива
 * ли проверка: включённый рубильник при остановленном планировщике выглядит
 * рабочим и не делает ничего, и узнать об этом 2-го числа по отсутствию лидов
 * — поздно.
 */
function AutoRunCard(): React.ReactElement {
  const [status, setStatus] = useState<CohortAutoStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .cohortAuto()
      .then((s) => {
        if (alive) setStatus(s);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, []);

  const flip = async (on: boolean) => {
    if (status === null || status.enabled === on) return;
    setBusy(true);
    setError(null);
    try {
      setStatus(await api.cohortAutoSet(on));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (
    <div className="seg" role="group" aria-label={t(TX.autoAria)}>
      <button
        type="button"
        aria-pressed={status?.enabled === false}
        disabled={busy || status === null}
        onClick={() => void flip(false)}
      >
        {t(TX.autoOff)}
      </button>
      <button
        type="button"
        className="seg-on"
        aria-pressed={status?.enabled === true}
        disabled={busy || status === null}
        onClick={() => void flip(true)}
      >
        {t(TX.autoOn)}
      </button>
    </div>
  );

  if (status === null) {
    return (
      <Card title={t(TX.autoTitle)} hint={t(TX.autoHint)} right={toggle}>
        {error !== null && <div className="note" style={{ color: "var(--bad)" }}>{error}</div>}
      </Card>
    );
  }

  const s = status;
  const every = s.check.everyMinutes ?? 15;
  const cohortMonth = monthLabel(s.next.eventFrom.slice(0, 7));
  const runMonth = (iso: string) => monthLabel(iso.slice(0, 7));

  /* Проверка считается остановившейся после трёх пропущенных: одна пропущенная
     — это перезапуск или долгий соседний прогон, три подряд — уже симптом. */
  const stale =
    s.check.lastAt === null || Date.now() - new Date(s.check.lastAt).getTime() > 3 * every * 60_000;
  const warnings: string[] = [];
  if (s.enabled) {
    if (!s.check.schedulerEnabled) warnings.push(t(TX.autoNoScheduler));
    else if (!s.check.scheduleEnabled) warnings.push(t(TX.autoNoSchedule));
    else if (stale) warnings.push(`${t(TX.autoStale)} ${ago(s.check.lastAt)}.`);
    if (s.check.lastStatus === "error" && s.check.lastError) {
      warnings.push(`${t(TX.autoCheckFailed)} ${s.check.lastError}`);
    }
  }

  let headline: string;
  if (!s.enabled) {
    headline = `${t(TX.autoNextOff)} ${dubaiMoment(s.next.dueAt)} ${t(TX.autoDubai)}`;
  } else if (s.verdict === "resume" && s.current !== null) {
    headline =
      `${t(TX.autoResumeA)} ${runMonth(s.current.auto_month)}${t(TX.autoResumeB)} ` +
      `${num(s.current.created)} ${t(TX.autoResumeOf)} ${num(s.current.requested)}${t(TX.autoResumeC)}`;
  } else if (s.verdict === "due") {
    headline = `${t(TX.autoDueA)} ${num(every)} ${t(TX.autoDueB)}`;
  } else {
    headline = `${t(TX.autoNextOn)} ${dubaiMoment(s.next.dueAt)} ${t(TX.autoDubai)}`;
  }

  return (
    <Card title={t(TX.autoTitle)} hint={t(TX.autoHint)} right={toggle}>
      <div style={{ display: "grid", gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{headline}</div>
        {s.verdict !== "resume" && (
          <div>
            {t(TX.autoCohort)} <b>{cohortMonth}</b> {t(TX.autoCohortNow)} {num(s.next.total)}{" "}
            {plural(s.next.total, TX.clientsWord)}, {t(TX.autoCanCreate)} {num(s.next.eligible)}.{" "}
            <span className="muted">{t(TX.autoRecount)}</span>
            {s.next.eligible > s.cap && (
              <>
                {" "}
                {t(TX.autoOverCapA)} {num(s.cap)} {t(TX.autoOverCapB)}
              </>
            )}
          </div>
        )}
        <div className="note">{t(TX.autoRules)}</div>
        <div className="muted" style={{ fontSize: 12 }}>
          {/* «Ещё не было» — только когда нет и идущего: про идущий сказано
              в первой строке, и «не было» рядом с «идёт» читалось бы как сбой. */}
          {s.last !== null
            ? `${t(TX.autoLast)} ${runMonth(s.last.auto_month)}: ${t(TX.autoLastCreated)} ${num(s.last.created)}, ` +
              `${t(TX.autoLastFailed)} ${num(s.last.failed)}, ${t(TX.autoLastSkipped)} ${num(s.last.skipped)}.`
            : s.current === null
              ? t(TX.autoNever)
              : ""}
          {s.changedAt !== null && (
            <>
              {" "}
              {t(TX.autoSwitched)} {s.changedBy ?? "—"}, {dubaiMoment(s.changedAt)}.
            </>
          )}{" "}
          {t(TX.autoEvery)} {num(every)} {t(TX.autoMin)}, {t(TX.autoLastCheck)} {ago(s.check.lastAt)}.
        </div>
        {warnings.map((w) => (
          <div key={w} className="note" style={{ color: "var(--bad)" }}>
            {w}
          </div>
        ))}
        {error !== null && <div className="note" style={{ color: "var(--bad)" }}>{error}</div>}
      </div>
    </Card>
  );
}

const pad = (n: number): string => String(n).padStart(2, "0");
const daysIn = (year: number, month: number): string => pad(new Date(year, month, 0).getDate());
